import { useMemo } from 'react';
import Select, { SingleValue, StylesConfig } from 'react-select';
import { ITipsFeedbackFilters, TFeedbackWorkflowStatus } from '../../../../interfaces/tips-feedback/ITipsFeedback';
import { FEEDBACK_FILTER_STATUSES } from '../utils';
import {
    ITipsFeedbackClientOption,
    ITipsFeedbackPropertyOption,
} from '../../../../store/users/usersApi';
import DateRangePicker from '../../../../shared/date-range-picker';
import { useTheme } from '../../../../utils/theme-context';

const SearchIcon = () => (
    <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
        <path
            d='M21 21l-4.35-4.35M10.8 18a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4Z'
            stroke='currentColor'
            strokeWidth='1.8'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
);

type FilterSelectOption = {
    value: string;
    label: string;
};

const TipsFeedbackFilters = ({
    totalElements,
    filters,
    loading,
    loadingFilterOptions,
    clientOptions,
    propertyOptions,
    downloadingExcel,
    sort,
    onSortChange,
    onChange,
    onApply,
    onClear,
    onDownloadExcel,
    onOpenAnalysis,
}: {
    totalElements: number;
    filters: ITipsFeedbackFilters;
    loading?: boolean;
    loadingFilterOptions?: boolean;
    clientOptions?: ITipsFeedbackClientOption[];
    propertyOptions?: ITipsFeedbackPropertyOption[];
    downloadingExcel?: boolean;
    sort: string;
    onSortChange: (sort: string) => void;
    onChange: (next: ITipsFeedbackFilters) => void;
    onApply: () => void;
    onClear: () => void;
    onDownloadExcel: () => void;
    onOpenAnalysis: () => void;
}) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const onTextFilterChange = (key: keyof ITipsFeedbackFilters, value: string) => {
        onChange({
            ...filters,
            [key]: value,
        });
    };

    const selectStyles: StylesConfig<FilterSelectOption, false> = {
        control: (base, state) => ({
            ...base,
            minHeight: '40px',
            borderRadius: '0.5rem',
            borderColor: isDark ? '#2f2f2f' : '#e5e7eb',
            boxShadow: 'none',
            backgroundColor: isDark ? '#000000' : '#ffffff',
            '&:hover': {
                borderColor: state.isFocused ? (isDark ? '#4b5563' : '#d1d5db') : (isDark ? '#2f2f2f' : '#e5e7eb'),
            },
        }),
        singleValue: (base) => ({
            ...base,
            color: isDark ? '#ffffff' : '#000000',
            fontSize: '0.875rem',
        }),
        input: (base) => ({
            ...base,
            color: isDark ? '#ffffff' : '#000000',
            fontSize: '0.875rem',
        }),
        placeholder: (base) => ({
            ...base,
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: '0.875rem',
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: isDark ? '#000000' : '#ffffff',
            border: `1px solid ${isDark ? '#2f2f2f' : '#e5e7eb'}`,
            borderRadius: '0.5rem',
            zIndex: 40,
        }),
        menuList: (base) => ({
            ...base,
            maxHeight: 220,
        }),
        option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isSelected
                ? (isDark ? '#1f2937' : '#f3f4f6')
                : isFocused
                    ? (isDark ? '#111827' : '#f9fafb')
                    : 'transparent',
            color: isDark ? '#ffffff' : '#000000',
            fontSize: '0.875rem',
            cursor: 'pointer',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: isDark ? '#9ca3af' : '#6b7280',
        }),
        indicatorSeparator: (base) => ({
            ...base,
            backgroundColor: isDark ? '#2f2f2f' : '#e5e7eb',
        }),
    };

    const clientSelectOptions = useMemo(
        () => [
            { value: '', label: 'All clients' },
            ...((clientOptions || []).map((client) => ({
                value: client.companyName || client.name || client.label,
                label: client.label,
            }))),
        ],
        [clientOptions],
    );

    const propertySelectOptions = useMemo(
        () => [
            { value: '', label: 'All properties' },
            ...((propertyOptions || []).map((property) => ({
                value: property.companyName,
                label: property.label || property.companyName,
            }))),
        ],
        [propertyOptions],
    );

    const selectedClient = useMemo(
        () =>
            clientSelectOptions.find((option) => option.value === (filters.client || ''))
            || clientSelectOptions[0],
        [clientSelectOptions, filters.client],
    );

    const selectedProperty = useMemo(
        () =>
            propertySelectOptions.find((option) => option.value === (filters.property || ''))
            || propertySelectOptions[0],
        [propertySelectOptions, filters.property],
    );

    const onClientChange = (option: SingleValue<FilterSelectOption>) => {
        onTextFilterChange('client', option?.value || '');
    };

    const onPropertyChange = (option: SingleValue<FilterSelectOption>) => {
        onTextFilterChange('property', option?.value || '');
    };

    return (
        <>
            <details open className='my-4 rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-black'>
                <summary className='cursor-pointer rounded-t-2xl border-b border-gray-100 bg-gray-50 px-5 py-5 text-sm font-semibold text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'>Apply Filters</summary>
                <div className='p-4 sm:p-6'>
                    <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
                <div className='relative min-w-0'>
                    <label htmlFor='feedback-client-filter' className='mb-1 block text-sm font-medium text-gray-500 dark:text-gray-300'>
                        Client
                    </label>
                    <Select
                        inputId='feedback-client-filter'
                        options={clientSelectOptions}
                        value={selectedClient}
                        onChange={onClientChange}
                        isDisabled={loadingFilterOptions}
                        isSearchable
                        isClearable={false}
                        styles={selectStyles}
                        noOptionsMessage={() => loadingFilterOptions ? 'Loading...' : 'No clients found'}
                    />
                </div>

                <div className='relative min-w-0'>
                    <label htmlFor='feedback-property-filter' className='mb-1 block text-sm font-medium text-gray-500 dark:text-gray-300'>
                        Property
                    </label>
                    <Select
                        inputId='feedback-property-filter'
                        options={propertySelectOptions}
                        value={selectedProperty}
                        onChange={onPropertyChange}
                        isDisabled={loadingFilterOptions}
                        isSearchable
                        isClearable={false}
                        styles={selectStyles}
                        noOptionsMessage={() => loadingFilterOptions ? 'Loading...' : 'No properties found'}
                    />
                </div>

                <div className='flex min-w-0 flex-col gap-1'>
                    <label className='block text-sm font-medium text-gray-500 dark:text-gray-300'>
                        Feedback Date Range (UTC)
                    </label>
                    <DateRangePicker
                        dateFrom={filters.dateFrom}
                        dateTo={filters.dateTo}
                        placeholder='Select feedback date range'
                        compact
                        onChange={(next) => onChange({
                            ...filters,
                            dateFrom: next.dateFrom,
                            dateTo: next.dateTo,
                        })}
                    />
                </div>

                <div className='flex min-w-0 flex-col gap-1'>
                    <label htmlFor='feedback-status-filter' className='block text-sm font-medium text-gray-500 dark:text-gray-300'>
                        Workflow status
                    </label>
                    <select id='feedback-status-filter' value={filters.workflowStatus || ''}
                        onChange={(event) => onChange({ ...filters, workflowStatus: event.target.value as TFeedbackWorkflowStatus || undefined })}
                        className='rounded-lg border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black px-3 py-2 text-sm dark:text-white'>
                        <option value=''>All statuses</option>
                        {FEEDBACK_FILTER_STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                    </select>
                </div>

                    </div>
                    <div className='mt-5 flex flex-wrap items-end justify-between gap-4'>
                        <div className='relative w-full sm:max-w-[380px]'>
                            <span className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-500'><SearchIcon /></span>
                            <input aria-label='Search feedback records' value={filters.search || ''} onChange={(event) => onTextFilterChange('search', event.target.value)} placeholder='Search Feedback'
                                className='min-h-[44px] w-full rounded-lg bg-gray-50 py-2 pl-10 pr-3 text-sm text-gray-900 dark:bg-gray-900 dark:text-white' />
                        </div>
                <div className='flex w-full flex-col gap-1 sm:w-[220px]'>
                    <label htmlFor='feedback-sort' className='block text-sm font-medium text-gray-500 dark:text-gray-300'>Sort by</label>
                    <select id='feedback-sort' value={sort} onChange={(event) => onSortChange(event.target.value)}
                        className='rounded-lg border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black px-3 py-2 text-sm dark:text-white'>
                        <option value='date,desc'>Newest date</option>
                        <option value='date,asc'>Oldest date</option>
                        <option value='property,asc'>Property A–Z</option>
                        <option value='property,desc'>Property Z–A</option>
                    </select>
                </div>

                    </div>
                    <div className='mt-6 flex flex-wrap items-center justify-between gap-3'>
                        <div className='flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-300'>
                            {filters.client && <span className='rounded bg-gray-100 px-2 py-1 dark:bg-gray-800'>Client: {filters.client}</span>}
                            {filters.property && <span className='rounded bg-gray-100 px-2 py-1 dark:bg-gray-800'>Property: {filters.property}</span>}
                            {(filters.dateFrom || filters.dateTo) && <span className='rounded bg-gray-100 px-2 py-1 dark:bg-gray-800'>Date: {filters.dateFrom || 'Any'} – {filters.dateTo || 'Any'}</span>}
                        </div>
                        <div className='ml-auto flex w-full justify-end gap-3 sm:w-auto'>
                            <button type='button' onClick={onClear} disabled={loading} className='min-h-[44px] flex-1 rounded-xl bg-gray-100 px-6 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 sm:flex-none'>Clear All</button>
                            <button type='button' onClick={onApply} disabled={loading} className='min-h-[44px] flex-1 rounded-xl bg-gray-800 px-9 py-2 text-sm font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-black sm:flex-none'>Show</button>
                        </div>
                    </div>
                </div>
            </details>
            <div className='mb-3 mt-6 flex flex-wrap items-center justify-between gap-3'>
                <p className='text-sm text-gray-600 dark:text-gray-300'>Showing {totalElements} Records</p>
                <div className='flex flex-wrap gap-3'>
                    <button type='button' onClick={onDownloadExcel} disabled={loading || downloadingExcel || totalElements <= 0}
                        className='inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-gray-800 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-black'>
                        {downloadingExcel ? 'Preparing...' : 'Export Feedback'}
                        <svg aria-hidden='true' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M12 3v12m-5-5 5 5 5-5M4 18v3h16v-3' /></svg>
                    </button>
                    <button type='button' onClick={onOpenAnalysis} className='min-h-[40px] rounded-lg border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-800 dark:border-gray-700 dark:text-gray-200'>Meta Analysis</button>
                </div>
            </div>
        </>
    );
};

export default TipsFeedbackFilters;
